import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React, { useCallback, useEffect, useState } from 'react';

import { MatterSelectorCombobox } from '@/components/matter/MatterSelectorCombobox';
import { Button } from '@/components/ui/catalyst/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  CmsCaseMatchType,
  CmsImportTaskSchemaWithDetails,
} from '@/services/api/generated/cmsImportsService';

interface MatterCellProps {
  task: CmsImportTaskSchemaWithDetails;
  matterId: string | null;
  matterName: string | null;
  firmId: string;
  onSaveMatch: (caseInternalId: string, matterId: string | null) => void;
  isSaving: boolean;
  matchConfidence?: number | null;
  matchReason?: string | null;
  matchType?: CmsCaseMatchType | null;
}

const formatMatchType = (
  matchType: CmsCaseMatchType | null | undefined,
): string => {
  if (!matchType) return '';
  switch (matchType) {
    case 'link_match':
      return 'Direct link match';
    case 'fuzzy_match':
      return 'Fuzzy match';
    case 'user_override':
      return 'Manual assignment';
    case 'no_match':
      return 'No match found';
    default:
      return matchType;
  }
};

export const MatterCell: React.FC<MatterCellProps> = ({
  task,
  matterId,
  matterName,
  firmId,
  onSaveMatch,
  isSaving,
  matchConfidence,
  matchReason,
  matchType,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState<string>(
    matterId || '',
  );
  const [hasSelection, setHasSelection] = useState(false);

  // Reset state when task changes
  useEffect(() => {
    setSelectedMatterId(matterId || '');
    setHasSelection(false);
    setIsEditing(false);
  }, [matterId]);

  const handleSelect = useCallback((newMatterId: string) => {
    setSelectedMatterId(newMatterId);
    setHasSelection(true);
  }, []);

  const handleSave = useCallback(() => {
    const caseInternalId = task.cms_task.internal_case_id;
    if (caseInternalId) {
      onSaveMatch(caseInternalId, selectedMatterId || null);
      setIsEditing(false);
      setHasSelection(false);
    }
  }, [task.cms_task.internal_case_id, selectedMatterId, onSaveMatch]);

  const handleCancel = useCallback(() => {
    setSelectedMatterId(matterId || '');
    setIsEditing(false);
    setHasSelection(false);
  }, [matterId]);

  // Editing mode (for matched cases that user wants to change)
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <MatterSelectorCombobox
          value={selectedMatterId}
          onChange={handleSelect}
          firmId={firmId}
          includeAllOption={false}
          className="flex-1 min-w-0"
          aria-label="Select matter"
        />
        <Button
          plain
          onClick={handleSave}
          disabled={isSaving}
          className="flex-shrink-0 p-1"
          aria-label="Save"
        >
          <CheckCircleIcon className="h-4 w-4 text-green-600" />
        </Button>
        <Button
          plain
          onClick={handleCancel}
          className="flex-shrink-0 p-1"
          aria-label="Cancel"
        >
          <XMarkIcon className="h-4 w-4 text-zinc-500" />
        </Button>
      </div>
    );
  }

  // Matched - show matter name + edit button with tooltip for match info
  if (matterId && matterName) {
    const hasMatchInfo = matchConfidence != null || matchReason || matchType;

    const content = (
      <div className="flex items-center gap-2 group">
        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
        <span className="truncate text-sm">{matterName}</span>
        <Button
          plain
          onClick={() => setIsEditing(true)}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1"
          aria-label={`Edit match for ${task.cms_task.case_name}`}
        >
          <PencilIcon className="h-3 w-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300" />
        </Button>
      </div>
    );

    if (hasMatchInfo) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-1">
              {matchConfidence != null && (
                <div>
                  {Math.round(
                    matchConfidence <= 1
                      ? matchConfidence * 100
                      : matchConfidence,
                  )}
                  % confidence
                </div>
              )}
              {matchReason && <div>{matchReason}</div>}
              {matchType && (
                <div className="text-zinc-400">
                  {formatMatchType(matchType)}
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  }

  // Unmatched - show combobox, then Save/Cancel after selection
  return (
    <div className="flex items-center gap-1">
      <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
      <MatterSelectorCombobox
        value={selectedMatterId}
        onChange={handleSelect}
        firmId={firmId}
        placeholder="Select matter..."
        includeAllOption={false}
        className="flex-1 min-w-0"
        aria-label="Select matter"
      />
      {hasSelection && (
        <>
          <Button
            plain
            onClick={handleSave}
            disabled={isSaving}
            className="flex-shrink-0 p-1"
            aria-label="Save"
          >
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          </Button>
          <Button
            plain
            onClick={handleCancel}
            className="flex-shrink-0 p-1"
            aria-label="Cancel"
          >
            <XMarkIcon className="h-4 w-4 text-zinc-500" />
          </Button>
        </>
      )}
    </div>
  );
};
